<script setup lang="ts">
import type { ComponentExposed } from "vue-component-type-helpers"
import type { Step } from "@/components/ui/Stepper/Stepper.vue"
import type { ToastHtmlElement } from "@/types"
import { useQuery, useQueryClient } from "@tanstack/vue-query"
import { useUrlSearchParams } from "@vueuse/core"
import { computed, nextTick, useTemplateRef } from "vue"
import { getCustomTemplate } from "@/api"
import { Stepper, Toast } from "@/components/ui"
import { useTranslation } from "@/composables"
import TemplateDetailsForm from "./TemplateDetailsForm.vue"
import TemplateLegalForm from "./TemplateLegalForm.vue"
import TemplateThemeForm from "./TemplateThemeForm.vue"

const props = withDefaults(defineProps<{ templateId?: number }>(), {
  templateId: undefined,
})

const { t } = useTranslation()

const stepperRef = useTemplateRef<ComponentExposed<typeof Stepper>>("stepper")
const queryString = useUrlSearchParams<{ step?: number }>()
const queryClient = useQueryClient()

const errorToast = useTemplateRef<ToastHtmlElement | null>("errorToast")

const currentStep = computed(() => {
  const defaultStep = 1
  const queryStep = Number(queryString.step)

  return (Number.isInteger(queryStep) && queryStep >= 1 && queryStep <= 3)
    ? `step-${queryStep}`
    : `step-${defaultStep}`
})

const { data: customTemplate } = useQuery({
  queryKey: ["getCustomTemplate", props.templateId],
  queryFn: () => getCustomTemplate(props.templateId),
  enabled: !!props.templateId,
})

const steps = computed<Step[]>(() => [
  { key: "step-1", label: t("template_create_steps.step_1"), state: "INCOMPLETE" },
  { key: "step-2", label: t("template_create_steps.step_2"), state: "INCOMPLETE", disabled: !customTemplate.value },
  { key: "step-3", label: t("template_create_steps.step_3"), state: "INCOMPLETE", disabled: !customTemplate.value },
])

async function invalidateTemplateQuery() {
  await queryClient.invalidateQueries({ queryKey: ["getCustomTemplate"] })
}

function nextStep(step: number) {
  stepperRef.value?.next()
  queryString.step = step
}

function prevStep(step: number) {
  scrollToTop()
  stepperRef.value?.previous()
  queryString.step = step
}

function scrollToTop() {
  nextTick(() => window.scrollTo({ top: 0, behavior: "instant" }))
}

function onError() {
  errorToast.value?.showToast()
}

function onSuccess(nextStepNo?: number) {
  if (nextStepNo)
    nextStep(nextStepNo)
  invalidateTemplateQuery()
}
</script>

<template>
  <div class="border rounded-xl p-4 md:p-6 mb-8">
    <Stepper ref="stepper" :steps="steps" :initial-step="currentStep" horizontal>
      <template #step-1>
        <TemplateDetailsForm
          :template-id="templateId"
          @save="scrollToTop"
          @error="onError"
          @success="onSuccess(2)"
        />
      </template>
      <template #step-2>
        <TemplateThemeForm
          :template-id="templateId"
          @save="scrollToTop"
          @back="prevStep(1)"
          @error="onError"
          @success="onSuccess(3)"
        />
      </template>
      <template #step-3>
        <TemplateLegalForm
          :template-id="templateId"
          @save="scrollToTop"
          @back="prevStep(2)"
          @error="onError"
          @success="onSuccess"
        />
      </template>
    </Stepper>
  </div>
  <Toast ref="errorToast" variant="danger" />
</template>
